/* eslint-disable */
import {
  CheckCircleFilled,
  ImportOutlined, InboxOutlined, WarningOutlined
} from '@ant-design/icons';
import {
  Button, Card, message, Modal, Space, Spin, Typography,
  UploadProps
} from 'antd';
import { ColumnProps } from 'antd/es/table';
import { RcFile } from 'antd/es/upload';
import Dragger from 'antd/es/upload/Dragger';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import HeaderPage from 'common/components/HeaderPage';
import PageTable from 'common/components/PageTable';
import { getAccessToken } from 'common/services/common/storage';
import { exportSalaryReportService } from 'common/services/extends/payrollPeriods';
import { ROUTE_PATHS } from 'common/utils/constant';
import { formatNumber } from 'common/utils/functions';
import roles, { getPermission } from 'configs/roles';

type ImportTable = {
  key: number,
  nameCol: string,
  CCCDCol: string,
  status: 'invalid' | 'valid',
  statusWarning: 'invalid-warning' | 'valid',
  errorStatusCol?: Array<string>,
  warningStatusCol?: Array<string>,
  position?: string,
  baseSalary: number
  training: number
  workOnHoliday: number
  survey: number
  phoneAllowance: number
  goFar: number
  setup: number
  stay: number
  eat: number
  ot: number
  warehouse: number
  income: number
  shiftCount: number
  workingDays: number
  totalPrice: number
  supportAnother: number
  note?: string
};

const Import: React.FC<ActiveRoles> = ({ roleOther }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const payrollPeriodId = searchParams.get('payrollPeriodId') || '';
  const staffFullTimeId = searchParams.get('staffFullTimeId') || '';
  const projectId = searchParams.get('projectId') || '';
  const { t } = useTranslation();
  const [fileList, setFileList] = React.useState<RcFile | null>(null);

  const [jsonFile, setJsonFile] = React.useState<string>('');
  const [isLoadingJsonFile, setIsLoadingJsonFile] = React.useState<boolean>(false);
  const loadedJSonFileRef = React.useRef<boolean>(false);

  const { mutate: downloadAttendanceRecords, isLoading: downloadLoading } = useMutation(
    ['downloadAttendanceRecords'],
    () => exportSalaryReportService(projectId, payrollPeriodId),
    {
      onSuccess: (data) => {
        if (data?.link) {
          window.open(data.link);
          message.success(t('message.exportSuccess'));
        }
      },
      onError: () => {
        message.error(t('message.exportError'));
      }
    }
  );

  const streamFile = async (fileRes: RcFile, preview: boolean) => {
    try {
      setIsLoadingJsonFile(true);
      const bodyForm = new FormData();
      bodyForm.append('file', fileRes);
      bodyForm.append('preview', preview ? '1' : '0');
      const token = getAccessToken();
      // request with form data to api for endpoint /api/v1/staffs/import
      const res = await fetch(`/api/v1/projects/${projectId}/payroll-periods/${payrollPeriodId}/import?payrollPeriodId=${payrollPeriodId}&projectId=${projectId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: bodyForm,
      });

      if (!res.ok) {
        console.log('Fail with status: ', res?.status);
        if (res.status === 401) {
          navigate('/login');
        } else {
          message.error(t('message.uploadError'));
        }
        setIsLoadingJsonFile(false);
        return;
      }
      const reader = res.body?.getReader();

      let result;
      const decoder = new TextDecoder();
      if (reader) {
        do {
          // eslint-disable-next-line no-await-in-loop
          result = await reader.read();
          const record = decoder.decode(result.value, { stream: true });
          if (preview) {
            setJsonFile((prev) => prev + record);
          }
        } while (!result.done);

        if (result.done) {
          loadedJSonFileRef.current = true;
          setIsLoadingJsonFile(false);
          if (!preview) {
            message.success(t('message.updateSuccess'));
            setFileList(null);
            setJsonFile('');
            navigate(`${ROUTE_PATHS.SALARY_CALCULATE_TABLE_DETAIL}?payrollPeriodId=${payrollPeriodId}&staffFullTimeId=${staffFullTimeId}&projectId=${projectId}`);
          }
        }
      }
    } catch (e) {
      setIsLoadingJsonFile(false);
      // Emtry
    }
  };

  const handleBeforeUpload = async (fileRes: RcFile): Promise<boolean> => {
    try {
      if (payrollPeriodId && projectId) {
        loadedJSonFileRef.current = false;
        setJsonFile('');
        setFileList(fileRes);
        streamFile(fileRes, true);
      }
    } catch {
      message.error('Import failed');
    }
    return false;
  };
  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    showUploadList: false,
    beforeUpload: handleBeforeUpload,
    disabled: isLoadingJsonFile,
  };

  const onCancel = () => {
    loadedJSonFileRef.current = true;
    setJsonFile('');
    setFileList(null);
  };

  const columns: ColumnProps<ImportTable>[] = [
    {
      title: t('contact.problem'),
      dataIndex: 'statusCol',
      key: 'statusCol',
      width: 200,
      fixed: 'left',
      sorter: {
        compare: (a: ImportTable, b: ImportTable) => a.status.localeCompare(b.status),
      },
      render: (text, record) => {
        if (record.status === 'valid' && record.statusWarning === 'valid') {
          return (
            <Space>
              <CheckCircleFilled style={{ color: '#52c41a' }} />
              <Typography.Text>Hợp lệ</Typography.Text>
            </Space>
          );
        }
        return (
          <ul style={{ paddingLeft: 0 }}>
            {
              record.errorStatusCol?.map((i, index) => (
                <li key={`col-status-${index.toString()}`} style={{ listStyle: 'none ' }}>
                  <Space size={12}>
                    <WarningOutlined style={{ color: '#f5222d' }} />
                    <Typography.Text>{i}</Typography.Text>
                  </Space>
                </li>
              ))
            }
            {
              record.warningStatusCol?.map((i, index) => (
                <li key={`col-status-${index.toString()}`} style={{ listStyle: 'none ' }}>
                  <Space size={12}>
                    <WarningOutlined style={{ color: '#f69320' }} />
                    <Typography.Text>{i}</Typography.Text>
                  </Space>
                </li>
              ))
            }
          </ul>
        );
      }
    },
    {
      title: t('system.name'),
      dataIndex: 'nameCol',
      key: 'nameCol',
      width: 100,
      render: (_value: any, _record) => <Typography.Text>{_value}</Typography.Text>
    },
    {
      title: t('employee.identityNumber'),
      dataIndex: 'CCCDCol',
      key: 'CCCDCol',
      width: 100,
      render: (_value: any, _record) => <Typography.Text>{_value}</Typography.Text>
    },
    {
      title: t('system.position'),
      dataIndex: 'position',
      key: 'position',
      width: 40,
      render: (_value: any, _record) => <Typography.Text>{_value}</Typography.Text>
    },
    {
      title: 'Số ca',
      dataIndex: 'shiftCount',
      key: 'shiftCount',
      width: 40,
      align: 'center',
      render: (_value: any, _record) => <Typography.Text>{formatNumber(_value)}</Typography.Text>
    },
    {
      title: 'Ngày công',
      dataIndex: 'workingDays',
      key: 'workingDays',
      align: 'center',
      width: 70,
      render: (_value: any, _record) => <Typography.Text>{formatNumber(_value)}</Typography.Text>
    },
    {
      title: 'Đơn giá',
      dataIndex: 'baseSalary',
      key: 'baseSalary',
      width: 40,
      align: 'center',
      render: (_value: any, _record) => <Typography.Text>{formatNumber(_value)}</Typography.Text>
    },
    {
      title: 'Thành tiền',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      width: 70,
      align: 'center',
      render: (_value: any, _record) => <Typography.Text>{formatNumber(_value)}</Typography.Text>
    },
    {
      title: 'Training',
      dataIndex: 'training',
      key: 'training',
      width: 40,
      align: 'center',
      render: (_value: any, _record) => <Typography.Text>{formatNumber(_value)}</Typography.Text>
    },
    {
      title: 'Làm ngày lễ',
      dataIndex: 'workOnHoliday',
      key: 'workOnHoliday',
      width: 60,
      align: 'center',
      render: (_value: any, _record) => <Typography.Text>{formatNumber(_value)}</Typography.Text>
    },
    {
      title: 'Khảo Sát',
      dataIndex: 'survey',
      key: 'survey',
      width: 40,
      align: 'center',
      render: (_value: any, _record) => <Typography.Text>{formatNumber(_value)}</Typography.Text>
    },
    {
      title: 'Hỗ trợ ĐT',
      dataIndex: 'phoneAllowance',
      key: 'phoneAllowance',
      width: 50,
      align: 'center',
      render: (_value: any, _record) => <Typography.Text>{formatNumber(_value)}</Typography.Text>
    },
    {
      title: 'Đi xa',
      dataIndex: 'goFar',
      key: 'goFar',
      width: 40,
      align: 'center',
      render: (_value: any, _record) => <Typography.Text>{formatNumber(_value)}</Typography.Text>
    },
    {
      title: 'Setup',
      dataIndex: 'setup',
      key: 'setup',
      width: 40,
      align: 'center',
      render: (_value: any, _record) => <Typography.Text>{formatNumber(_value)}</Typography.Text>
    },
    {
      title: 'Ở',
      dataIndex: 'stay',
      key: 'stay',
      width: 40,
      align: 'center',
      render: (_value: any, _record) => <Typography.Text>{formatNumber(_value)}</Typography.Text>
    },
    {
      title: 'Ăn',
      dataIndex: 'eat',
      key: 'eat',
      width: 40,
      align: 'center',
      render: (_value: any, _record) => <Typography.Text>{formatNumber(_value)}</Typography.Text>
    },
    {
      title: 'Thêm giờ',
      dataIndex: 'ot',
      key: 'ot',
      width: 40,
      align: 'center',
      render: (_value: any, _record) => <Typography.Text>{formatNumber(_value)}</Typography.Text>
    },
    {
      title: 'Kho',
      dataIndex: 'warehouse',
      key: 'warehouse',
      width: 40,
      align: 'center',
      render: (_value: any, _record) => <Typography.Text>{formatNumber(_value)}</Typography.Text>
    },
    {
      title: 'Hỗ trợ khác',
      dataIndex: 'supportAnother',
      key: 'supportAnother',
      width: 60,
      align: 'center',
      render: (_value: any, _record) => <Typography.Text>{formatNumber(_value)}</Typography.Text>
    },
    {
      title: 'Ghi chú hỗ trợ khác',
      dataIndex: 'note',
      key: 'note',
      width: 180,
      render: (_value: any, _record) => <Typography.Text>{_value}</Typography.Text>
    },
  ];

  const tableData = useMemo(() => {
    if (!loadedJSonFileRef.current) {
      console.log('Chua load xong');

      return null;
    }
    if (jsonFile === '') {
      console.log('Reset jsonFile');
      return null;
    }

    const lines = jsonFile.split('\n');
    const rowData: ImportTable[] = [];
    let totalData: number = 0;
    let errorsData: number = 0;
    let warningsData: number = 0;
    lines.forEach((line) => {
      if (line.trim()) {
        const json = JSON.parse(line);
        if (json.status === 'processing') {
          const { row, total, errors, warnings } = json.data;

          if (!row) {
            if (Object.prototype.hasOwnProperty.call(json.data, 'warnings')) {
              warningsData = warnings;
            }
            if (Object.prototype.hasOwnProperty.call(json.data, 'total')) {
              totalData = total;
            }
            if (Object.prototype.hasOwnProperty.call(json.data, 'errors')) {
              errorsData = errors;
            }
            return;
          }

          const rowResult: ImportTable = {
            ...row,
            key: row?.rowNumber,
            nameCol: row?.fullname || '',
            CCCDCol: row.idCardNumber,
            status: row.errors?.length > 0 ? 'invalid' : 'valid',
            statusWarning: row.warnings && row.warnings?.length > 0 ? 'invalid-warning' : 'valid',
            errorStatusCol: row.errors,
            warningStatusCol: row.warnings,
            position: row.position,
            totalPrice: row.baseSalary * row.shiftCount
          };
          console.log('rowResult: ', rowResult);
          rowData.push(rowResult);
        }
      }
    });
    console.log('rowData: ', rowData);

    return {
      total: totalData,
      errors: errorsData,
      rows: rowData,
      warnings: warningsData
    };
  }, [jsonFile, loadedJSonFileRef.current]);

  return (
    <div className="p-employeeImport">
      <HeaderPage
        fixed
        title={t('salary.import')}
        rightHeader={(
          tableData && (
            <Space>
              <Button
                type="primary"
                disabled={!tableData.rows.length || tableData.errors > 0}
                onClick={() => {
                  Modal.confirm({
                    className: 't-pagetable_deleteLanguageModal',
                    autoFocusButton: 'cancel',
                    okText: t('system.ok'),
                    cancelText: t('system.cancel'),
                    cancelButtonProps: {
                      type: 'primary',
                    },
                    okButtonProps: {
                      type: 'default',
                    },
                    title: t('employee.importConfirm'),
                    onOk: () => {
                      if (fileList && payrollPeriodId && projectId) {
                        streamFile(fileList, false);
                      }
                    },
                  });
                }}
              >
                {t('system.ok')}
              </Button>
              <Button onClick={onCancel} disabled={isLoadingJsonFile}>
                {t('system.cancel')}
              </Button>
            </Space>
          ))}
      />
      <Spin spinning={isLoadingJsonFile}>
        <div className="t-mainlayout_wrapper">
          <Card
            className="p-employeeImport_card"
            title={(
              <Space style={{ width: '100%', justifyContent: 'space-between' }} direction="horizontal">
                {t('system.import')}
                {tableData ? null : (
                  <Button
                    type="primary"
                    disabled={downloadLoading
                      || !getPermission(roleOther, roles.ATTENDANCE_RECORD_EXPORT_INDEX)}
                    onClick={() => downloadAttendanceRecords()}
                    loading={downloadLoading}
                  >
                    <Space>
                      <ImportOutlined />
                      {t('system.downloadSampleFile')}
                    </Space>
                  </Button>
                )}
              </Space>
            )}
          >
            {tableData ? (
              <div>
                <div className="p-employeeImport_card_general">
                  <Space className="p-employeeImport_card_general-item" direction="vertical">
                    <Typography.Text className="p-employeeImport_card_general_title">Tổng số dòng dữ liệu</Typography.Text>
                    <Space className="p-employeeImport_card_general_wrapText">
                      <Typography.Text>{tableData.total}</Typography.Text>
                    </Space>
                  </Space>
                  <Space className="p-employeeImport_card_general-item success" direction="vertical">
                    <Typography.Text className="p-employeeImport_card_general_title">Số dòng hợp lệ</Typography.Text>
                    <Space className="p-employeeImport_card_general_wrapText">
                      <CheckCircleFilled style={{ color: '#52c41a' }} />
                      <Typography.Text>{tableData.total - tableData.errors}</Typography.Text>
                    </Space>
                  </Space>
                  <Space className="p-employeeImport_card_general-item error" direction="vertical">
                    <Typography.Text className="p-employeeImport_card_general_title">Số dòng lỗi</Typography.Text>
                    <Space className="p-employeeImport_card_general_wrapText">
                      <WarningOutlined style={{ color: '#f5222d' }} />
                      <Typography.Text>
                        {tableData.errors}
                      </Typography.Text>
                    </Space>
                  </Space>
                  <Space className="p-employeeImport_card_general-item warning" direction="vertical">
                    <Typography.Text className="p-employeeImport_card_general_title">Số dòng cảnh báo</Typography.Text>
                    <Space className="p-employeeImport_card_general_wrapText">
                      <WarningOutlined style={{ color: '#f69320' }} />
                      <Typography.Text>
                        {tableData.warnings}
                      </Typography.Text>
                    </Space>
                  </Space>
                </div>
                <div className="p-employeeImport_card_table">
                  <PageTable
                    noCheckbox
                    isLoading={false}
                    tableProps={{
                      columns,
                      pageData: tableData.rows || [],
                      initShowColumns: ['statusCol', 'nameCol', 'CCCDCol', 'position', 'shiftCount', 'workingDays', 'baseSalary', 'totalPrice', 'training', 'workOnHoliday', 'survey', 'phoneAllowance', 'goFar', 'setup', 'stay', 'eat', 'ot', 'warehouse', 'supportAnother', 'note'],
                      noBaseCol: true,
                      noDeleteLanguage: true,
                      rowClassName: (record) => (record.status === 'valid' ? 'p-employeeImport_card_table-validRow' : 'p-employeeImport_card_table-invalidRow'),
                      isHidePagination: true,
                      scroll: { x: 2900, y: 60 * 15 }
                    }}
                  />
                </div>
              </div>
            ) : (
              <Dragger
                {...uploadProps}
                disabled={isLoadingJsonFile
                  || !getPermission(roleOther, roles.ATTENDANCE_RECORD_IMPORT)}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  {' '}
                  {t('media.dndText')}
                </p>
              </Dragger>
            )}
          </Card>
        </div>
      </Spin>
    </div>
  );
};

export default Import;
